import { useCallback, useEffect, useState } from 'react';
import { Alert, Anchor, Badge, Button, Checkbox, Group, PasswordInput, Stack, Text, TextInput } from '@mantine/core';
import { ActionButton, Part, PART_GAP } from '../../ui/SettingsParts';
import { Brand } from '../../ui/Brand';
import { notifications } from '@mantine/notifications';
import { ApiError } from '../../infra/api';
import { pushPermission, subscribePush } from '../../infra/push';
import { useIsOwner, useTabroomApi, type WatchEvent, type WatcherStatus } from './useTabroom';
import { SMS_CONSENT_TEXT } from './smsConsent';

const when = (ms: number | null) => (ms ? new Date(ms).toLocaleString([], { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—');
const message = (e: unknown) => (e instanceof ApiError && e.body && typeof e.body === 'object' && 'message' in e.body ? String(e.body.message) : e instanceof Error ? e.message : 'Something went wrong.');

/** Default end for tournament mode: 11pm, two days from now (a typical Fri–Sun tournament). */
const defaultUntil = () => {
  const d = new Date(Date.now() + 2 * 86_400_000);
  d.setHours(23, 0, 0, 0);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
};

interface Snapshot {
  email: string | null;
  history: { tournament: string; year: string; dates: string; division: string; rounds: string }[];
  upcoming: string[];
  inbox: { count: number; latest: { subject: string; tournament: string | null; sentAt: string }[] };
  checkedAt: number;
}
interface TestResult { pushDevices: number; sms: { ok: boolean; status: string; sid?: string; code?: number; message?: string } | null }

/** What a test actually did, in words. */
const testMessage = (r: TestResult) => {
  const push = `Push sent to ${r.pushDevices} device${r.pushDevices === 1 ? '' : 's'}.`;
  if (!r.sms) return push;
  if (r.sms.ok) return `${push} Twilio accepted the text (${r.sms.status}). If it doesn't arrive, Twilio's message log has the reason; error 30032 means toll-free verification is still pending.`;
  return `${push} The text wasn't sent: ${r.sms.message ?? r.sms.status}${r.sms.code ? ` (Twilio error ${r.sms.code})` : ''}.`;
};

/** Proof the connection works: account, inbox, upcoming and recent judging, straight from Tabroom. */
const SnapshotView = ({ snap }: { snap: Snapshot }) => (
  <Stack gap={4} p="xs" style={{ border: '1px solid var(--grid-line)', borderRadius: 6 }} data-testid="tabroom-snapshot">
    <Text size="xs"><b>✓ Signed in to Tabroom</b>{snap.email ? ` as ${snap.email}` : ''} · checked {new Date(snap.checkedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</Text>
    <Text size="xs"><b>Inbox:</b> {snap.inbox.count} message{snap.inbox.count === 1 ? '' : 's'}{snap.inbox.latest.length ? '. Latest:' : ''}</Text>
    {snap.inbox.latest.map((m, i) => (
      <Text key={i} size="xs" c="dimmed" pl="sm" lineClamp={1}>{new Date(m.sentAt).toLocaleDateString([], { month: 'short', day: 'numeric' })} · {m.subject}{m.tournament ? ` (${m.tournament})` : ''}</Text>
    ))}
    <Text size="xs"><b>Upcoming judging:</b> {snap.upcoming.length ? '' : 'nothing listed right now'}</Text>
    {snap.upcoming.slice(0, 4).map((l, i) => <Text key={i} size="xs" c="dimmed" pl="sm" lineClamp={1}>{l}</Text>)}
    {snap.history.length > 0 && <Text size="xs"><b>Recent judging:</b></Text>}
    {snap.history.slice(0, 3).map((h, i) => (
      <Text key={i} size="xs" c="dimmed" pl="sm" lineClamp={1}>{h.tournament} · {h.dates || h.year}{h.division ? ` · ${h.division}` : ''}{h.rounds ? ` · Rounds ${h.rounds}` : ''}</Text>
    ))}
  </Stack>
);

const LABEL: Record<WatcherStatus['status'], { text: string; color: string }> = {
  disconnected: { text: 'Not connected', color: 'gray' },
  connected: { text: 'Connected', color: 'blue' },
  watching: { text: 'Watching', color: 'green' },
  expired: { text: 'Session ended: reconnect', color: 'orange' },
};

/** Text alerts: opt in with a number and explicit consent (recorded with the exact wording). Submits below its fields. */
const SmsOptIn = ({ busy, act, call }: {
  busy: string | null; act: (n: string, fn: () => Promise<unknown>, done?: string) => Promise<void>; call: ReturnType<typeof useTabroomApi>;
}) => {
  const [phone, setPhone] = useState('');
  const [agreed, setAgreed] = useState(false);
  const e164 = /^\+[1-9]\d{7,14}$/.test(phone.replace(/[\s()-]/g, ''));
  return (
    <Stack gap="sm" maw={560} data-testid="sms-optin">
      <TextInput size="sm" label="Mobile number" placeholder="+15551234567" value={phone} onChange={(e) => setPhone(e.currentTarget.value)} autoComplete="tel" />
      <Checkbox
        size="xs"
        checked={agreed}
        onChange={(e) => setAgreed(e.currentTarget.checked)}
        label={<Text size="xs">{SMS_CONSENT_TEXT} <Anchor href="/legal/terms" target="_blank" size="xs">Terms</Anchor> · <Anchor href="/legal/privacy" target="_blank" size="xs">Privacy</Anchor></Text>}
        data-testid="sms-consent"
      />
      <Group>
        <Button size="sm" variant="filled" disabled={!agreed || !e164} loading={busy === 'smson'} onClick={() => void act('smson', () => call('/sms', { to: phone.replace(/[\s()-]/g, ''), consent: true, consentText: SMS_CONSENT_TEXT }), 'Text alerts on.')}>Turn on text alerts</Button>
      </Group>
    </Stack>
  );
};

/**
 * Settings: the owner's personal Tabroom watcher. Connect once (one of Tabroom's 3 sign-in slots), turn on tournament
 * mode, get pushes/texts for blasts and new assignments. Owner (admin) only.
 */
export const TabroomCard = () => {
  const owner = useIsOwner();
  const call = useTabroomApi();
  const [status, setStatus] = useState<WatcherStatus | null>(null);
  const [events, setEvents] = useState<WatchEvent[]>([]);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [until, setUntil] = useState(defaultUntil);
  const [busy, setBusy] = useState<string | null>(null);
  const [snap, setSnap] = useState<Snapshot | null>(null);

  const refresh = useCallback(async () => {
    try {
      setStatus(await call<WatcherStatus>('/status'));
      setEvents((await call<{ events: WatchEvent[] }>('/events')).events.slice(-8).reverse());
    } catch { /* offline */ }
  }, [call]);

  useEffect(() => {
    if (!owner) return;
    void refresh();
    const id = setInterval(() => void refresh(), 30_000);
    return () => clearInterval(id);
  }, [owner, refresh]);

  if (!owner) return null;

  const act = async (name: string, fn: () => Promise<unknown>, done?: string) => {
    setBusy(name);
    try {
      const r = await fn();
      if (r && typeof r === 'object' && 'status' in r) setStatus(r as WatcherStatus);
      if (done) notifications.show({ color: 'green', message: done });
      await refresh();
    } catch (e) {
      notifications.show({ color: 'yellow', title: 'Tabroom', message: message(e) });
    } finally {
      setBusy(null);
    }
  };

  const s = status?.status ?? 'disconnected';
  const hasSession = s === 'connected' || s === 'watching';
  const granted = pushPermission() === 'granted';
  return (
    <Stack gap={PART_GAP} data-testid="tabroom-card">
      <Part
        title="How it works"
        lead={<>Connect once with your Tabroom sign-in. During tournament mode, <Brand /> reads your own Tabroom inbox and assignments every 2–3 minutes and tells you about new assignments, room and time changes, and urgent messages from tab. It only reads: nothing is ever posted to Tabroom, and nobody else's account is touched.</>}
      />

      <Part
        title="Tabroom sign-in"
        right={<Badge variant="light" color={LABEL[s].color} data-testid="tabroom-status">{LABEL[s].text}</Badge>}
        actions={hasSession ? (
          <>
            <ActionButton loading={busy === 'check'} onClick={() => void act('check', async () => { setSnap(null); setSnap(await call<Snapshot>('/check', {})); })} data-testid="tabroom-check">Check connection</ActionButton>
            <ActionButton danger loading={busy === 'disconnect'} onClick={() => confirm('Stop watching and sign out of Tabroom?') && void act('disconnect', () => call('/disconnect', {}), 'Disconnected from Tabroom.')}>Disconnect</ActionButton>
          </>
        ) : undefined}
        lead={hasSession
          ? <>Connected {when(status!.connectedAt)}. The session lasts until about {when(status!.tokenExpiresAt)}.{status!.lastCheck ? ` Last check ${when(status!.lastCheck)}.` : ''}{status!.lastError ? ` Last problem: ${status!.lastError}` : ''} Check reads your account right now, to show it's working; Disconnect signs out and frees the Tabroom sign-in slot.</>
          : 'Your password is used once, to sign in, and is never stored. Only the session cookie Tabroom returns is kept, encrypted.'}
      >
        {hasSession ? (
          snap && <SnapshotView snap={snap} />
        ) : (
          <form onSubmit={(e) => { e.preventDefault(); void act('connect', async () => { const r = await call('/connect', { username, password }); setPassword(''); return r; }, 'Connected to Tabroom.'); }}>
            <Stack gap="md" maw={560}>
              <Alert color="yellow" p="xs">
                <Text size="xs">
                  Tabroom allows 3 signed-in devices, and a new sign-in signs out the oldest. Connecting uses one of those, so connect before the tournament rather than mid-round, and don't sign in on a fourth device while it's connected.
                </Text>
              </Alert>
              <Group grow align="flex-end">
                <TextInput size="sm" label="Tabroom email" autoComplete="username" value={username} onChange={(e) => setUsername(e.currentTarget.value)} />
                <PasswordInput size="sm" label="Password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.currentTarget.value)} />
              </Group>
              <Group><Button size="sm" variant="filled" type="submit" loading={busy === 'connect'} disabled={!username || !password}>{s === 'expired' ? 'Reconnect' : 'Connect Tabroom'}</Button></Group>
            </Stack>
          </form>
        )}
      </Part>

      <Part
        title="Tournament mode"
        lead="While it's on, Tabroom is checked every 2–3 minutes. It stops itself at the time you set, so it never runs between tournaments."
      >
        {s === 'watching' ? (
          <Group gap="sm">
            <Text size="sm">Watching until {when(status!.watchUntil)}.</Text>
            <Button size="sm" variant="default" loading={busy === 'stop'} onClick={() => void act('stop', () => call('/stop', {}))}>Stop</Button>
          </Group>
        ) : (
          <Stack gap="xs">
            <Group gap="sm" align="flex-end">
              <TextInput size="sm" type="datetime-local" label="Until" value={until} onChange={(e) => setUntil(e.currentTarget.value)} disabled={!hasSession} />
              <Button size="sm" variant="filled" disabled={!hasSession} loading={busy === 'watch'} onClick={() => void act('watch', () => call('/watch', { until: new Date(until).getTime() }), 'Tournament mode on: checking Tabroom every few minutes.')}>Start tournament mode</Button>
            </Group>
            {!hasSession && <Text size="xs" c="dimmed" data-testid="tournament-mode-hint">Sign in to Tabroom above first. Tournament mode stays off until then.</Text>}
          </Stack>
        )}
      </Part>

      <Part
        title="Browser notifications"
        actions={
          <>
            <ActionButton primary={!granted} loading={busy === 'push'} onClick={() => void act('push', async () => {
              const { publicKey } = await call<{ publicKey: string | null }>('/vapid');
              if (!publicKey) throw new Error('Notifications aren’t set up on the server.');
              return call('/push', await subscribePush(publicKey));
            }, 'Notifications on for this device.')}>
              {granted ? 'Re-register this device' : 'Enable on this device'}
            </ActionButton>
            <ActionButton loading={busy === 'test'} onClick={() => void act('test', async () => { notifications.show({ message: testMessage(await call<TestResult>('/test', { sms: false })) }); })}>Send a test push</ActionButton>
          </>
        }
        lead={<>Push notifications from this browser, on every device you enable. Each device asks for permission once. On iPad, install <Brand /> to the Home Screen and enable notifications from there; Safari tabs can't receive them. A test goes to every enabled device, never by text. {status?.pushDevices ?? 0} device{status?.pushDevices === 1 ? '' : 's'} enabled so far.</>}
      />

      <Part
        title="Text alerts (SMS)"
        actions={status?.smsTo ? (
          <>
            {status.smsConfigured && <ActionButton loading={busy === 'sms'} onClick={() => void act('sms', async () => { const r = await call<TestResult>('/test', { sms: true }); notifications.show({ color: r.sms?.ok ? 'green' : 'yellow', autoClose: 12_000, message: testMessage(r) }); })}>Send a test text</ActionButton>}
            <ActionButton danger loading={busy === 'smsoff'} onClick={() => void act('smsoff', () => call('/sms', undefined, 'DELETE'), 'Text alerts off. Your number was removed.')}>Stop text alerts</ActionButton>
          </>
        ) : undefined}
        lead={status?.smsTo
          ? <>Texts go to {status.smsTo}, opted in {when(status.smsConsentedAt)}. Reply STOP to any text to opt out, or stop them here; your number is removed either way. A test sends one text to your number (and a push to enabled devices).{status.smsConfigured ? '' : ' Texting isn’t set up on the server yet.'}</>
          : <>A text for the things a push might not reach you for: new assignments and urgent messages from tab, only during tournament mode. Message and data rates may apply. <Anchor href="/legal/sms" target="_blank" size="sm">How the program works</Anchor> · <Anchor href="/legal/terms" target="_blank" size="sm">Terms</Anchor> · <Anchor href="/legal/privacy" target="_blank" size="sm">Privacy</Anchor></>}
      >
        {!status?.smsTo && <SmsOptIn busy={busy} act={act} call={call} />}
      </Part>

      {events.length > 0 && (
        <Part title="Recent" lead="What the watcher noticed lately, newest first.">
          <Stack gap={4}>
            {events.map((e) => (
              <Text key={e.id} size="sm" c={e.level === 'none' ? 'dimmed' : undefined}>
                {new Date(e.at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })} · <b>{e.title}</b> {e.body}
              </Text>
            ))}
          </Stack>
        </Part>
      )}
    </Stack>
  );
};
